/**
 * Kinetika va aralashma xossalari — CSTR/PFR uchun umumiy.
 *
 * TENGLAMALAR
 *   k(T)   = k0 · exp(−Ea/(R·T))                       Arrhenius
 *   −r_A   = k(T) · C_A^n                              n-tartibli qaytmas reaksiya
 *   ΔT_ad  = (−ΔH_r)·C_A0/(ρ·cp)                       adiabatik harorat ko'tarilishi (X = 1)
 *   Da     = k(T)·τ·C_A0^(n−1)                         Damköhler soni (CSTR/PFR uchun bir xil)
 *
 * QABUL QILINGAN SODDALASHTIRISHLAR (ASSUMPTION)
 *   1. Suyuq faza, zichlik va cp o'zgarmas (harorat va konversiyaga bog'liq emas);
 *      `solvent` berilsa xossalar kirish haroratida olinadi.
 *   2. Hajm o'zgarishi yo'q (ε_A = 0): hajmiy sarf kirish = chiqish.
 *   3. Bitta qaytmas reaksiya; teskari reaksiya, yonaki reaksiyalar yo'q.
 *   4. ΔH_r haroratga bog'liq emas (Kirchhoff tuzatishi yo'q).
 *   5. C_A < 0 bo'lsa tezlik 0 (sonli himoya; kasr tartibda NaN bo'lmasin).
 *
 * MANBA: Levenspiel, "Chemical Reaction Engineering", 3-nashr, 2–5 boblar;
 *        Fogler, "Elements of Chemical Reaction Engineering", 3, 11–12 boblar.
 */
import { R_GAS, heatCapacity, liquidDensity } from '../../properties';
import type { WarningSink } from '../../types';
import { catalystActivity, getReaction } from '../../reactions';
import { isLibraryRef, type ReactionMixture, type ReactionParams, type ReactionSpec } from './types';

/**
 * Kutubxona havolasini lab reaktori parametrlariga ochish:
 *   a = |ν_kalit|, b = ν_mahsulot (birinchi musbat), n = orders[kalit] yoki a,
 *   k0 = k0_lib · a(t_kat), Ea, ΔH_r(A uchun) = ΔH_r,lib / a.
 * Qaytar reaksiya (equilibrium) lab modellarida qo'llanmaydi — xato.
 */
export function resolveReaction(spec: ReactionSpec): ReactionParams {
  if (!isLibraryRef(spec)) return spec;
  const r = getReaction(spec.library);
  if (r.equilibrium) throw new Error(`reaction ${r.id}: lab reactors (cstr/pfr) support irreversible library reactions only`);
  const a = -(r.stoich[r.key] as number);
  const product = Object.entries(r.stoich).find(([, nu]) => nu > 0);
  const b = product ? product[1] : a;
  const out: ReactionParams = {
    k0: spec.k0 ?? r.kinetics.k0 * catalystActivity(r, spec.catalystAge ?? 0),
    Ea: spec.Ea ?? r.kinetics.Ea,
    order: spec.order ?? r.kinetics.orders?.[r.key] ?? a,
    stoichiometry: spec.stoichiometry ?? { a, b },
    dHr: spec.dHr ?? r.dHr / a,
  };
  if (spec.fluid) out.fluid = spec.fluid;
  if (spec.solvent) out.solvent = spec.solvent;
  return out;
}

/** Arrhenius: k(T), birligi k0 bilan bir xil */
export function arrhenius(k0: number, Ea: number, T: number): number {
  return k0 * Math.exp(-Ea / (R_GAS * T));
}

/** −r_A = k·C_A^n, mol/(m³·s). C_A ≤ 0 → 0. */
export function reactionRate(k: number, CA: number, order: number): number {
  if (CA <= 0) return 0;
  return k * Math.pow(CA, order);
}

/** d(−r_A)/dC_A = n·k·C_A^(n−1); C_A ≤ 0 → 0 (kasr tartibda cheksizlikdan himoya). */
export function rateDerivative(k: number, CA: number, order: number): number {
  if (CA <= 0) return 0;
  return order * k * Math.pow(CA, order - 1);
}

/** Damköhler soni Da = k·τ·C_A0^(n−1) */
export function damkohler(k: number, tau: number, CA0: number, order: number): number {
  if (CA0 <= 0) return order === 1 ? k * tau : 0;
  return k * tau * Math.pow(CA0, order - 1);
}

/** Params tekshiruvi — dasturchi xatosi bo'lsa throw. `unit` xabar prefiksi. */
export function validateReaction(unit: string, r: ReactionParams): void {
  if (!(r.k0 >= 0)) throw new Error(`${unit}: reaction.k0 must be ≥ 0`);
  if (!(r.Ea >= 0)) throw new Error(`${unit}: reaction.Ea must be ≥ 0`);
  const n = r.order ?? 1;
  if (!(n > 0)) throw new Error(`${unit}: reaction.order must be > 0`);
  if (r.stoichiometry) {
    if (!(r.stoichiometry.a > 0) || !(r.stoichiometry.b > 0))
      throw new Error(`${unit}: reaction.stoichiometry coefficients must be > 0`);
  }
  if (r.fluid) {
    if (!(r.fluid.density > 0)) throw new Error(`${unit}: reaction.fluid.density must be > 0`);
    if (!(r.fluid.cp > 0)) throw new Error(`${unit}: reaction.fluid.cp must be > 0`);
  } else if (!r.solvent) {
    throw new Error(`${unit}: reaction.fluid or reaction.solvent is required`);
  }
}

/**
 * Aralashma xossalari (ASSUMPTION 1): `fluid` bo'lsa u, aks holda `solvent`
 * xossalari `T` da (kirish harorati beriladi).
 */
export function mixtureProperties(
  r: ReactionParams,
  T: number,
  warnings?: WarningSink,
): ReactionMixture {
  let density: number;
  let cp: number;
  if (r.fluid) {
    density = r.fluid.density;
    cp = r.fluid.cp;
  } else {
    const s = r.solvent as string;
    density = liquidDensity(s, T, warnings);
    cp = heatCapacity(s, 'liquid', T, warnings);
  }
  const st = r.stoichiometry ?? { a: 1, b: 1 };
  return {
    density,
    cp,
    rhoCp: density * cp,
    order: r.order ?? 1,
    yieldB: st.b / st.a,
    heatRelease: -(r.dHr ?? 0),
  };
}

/** Adiabatik harorat ko'tarilishi (to'liq konversiyada), K */
export function adiabaticRise(mix: ReactionMixture, CA0: number): number {
  return (mix.heatRelease * CA0) / mix.rhoCp;
}
